import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { SpinnerView } from "../elements/Spinners";

// Lazy load all FundingKit containers
const FundingKitContainer = lazy(() =>
  import("../containers/FundingKit/FundingKitContainer").then((m) => ({
    default: m.FundingKitContainer,
  }))
);

const FKUpgradeContainer = lazy(() =>
  import("../containers/FundingKit/containers/FKUpgradeContainer").then((m) => ({
    default: m.FKUpgradeContainer,
  }))
);

const FKEventsContainer = lazy(() =>
  import("../containers/FundingKit/containers/FKEvents/FKEventsContainer").then(
    (m) => ({ default: m.FKEventsContainer })
  )
);

const FKEventDetails = lazy(() =>
  import("../containers/FundingKit/containers/FKEvents/FKEventDetails").then(
    (m) => ({ default: m.FKEventDetails })
  )
);

const FundingKitResearchInsightsContainer = lazy(() =>
  import(
    "../containers/FundingKit/ResearchInsights/ResearchInsightsContainer"
  ).then((m) => ({ default: m.FundingKitResearchInsightsContainer }))
);

const FundingKitResearchInsightView = lazy(() =>
  import("../containers/FundingKit/ResearchInsights/ResearchInsightView").then(
    (m) => ({ default: m.FundingKitResearchInsightView })
  )
);

const FKDealsContainer = lazy(() =>
  import("../containers/FundingKit/containers/FKDeals/FKDealsContainer").then(
    (m) => ({ default: m.FKDealsContainer })
  )
);

const FKIndustryPulseContainer = lazy(() =>
  import(
    "../containers/FundingKit/containers/FKIndustryPulse/FKIndustryPulseContainer"
  ).then((m) => ({ default: m.FKIndustryPulseContainer }))
);

const FKMarketIntelligenceContainer = lazy(() =>
  import("../containers/FundingKit/FKMarketIntelligenceContainer").then((m) => ({
    default: m.FKMarketIntelligenceContainer,
  }))
);

const FKTrendDetailContainer = lazy(() =>
  import(
    "../containers/FundingKit/containers/FKTrendDetail/FKTrendDetailContainer"
  ).then((m) => ({ default: m.FKTrendDetailContainer }))
);

const FKCFAContainer = lazy(() =>
  import("../containers/FundingKit/containers/FKCFA/FKCFAContainer").then(
    (m) => ({ default: m.FKCFAContainer })
  )
);

const FKCFAContainerView = lazy(() =>
  import("../containers/FundingKit/containers/FKCFA/FKCFAContainerView").then(
    (m) => ({ default: m.FKCFAContainerView })
  )
);

const FKIAListContainer = lazy(() =>
  import(
    "../containers/FundingKit/containers/FKInvestorsAnalysis/FKIAListContainer"
  ).then((m) => ({ default: m.FKIAListContainer }))
);

const FKSimilarStartupsContainer = lazy(() =>
  import("../containers/FundingKit/FKSimilarStartupsContainer").then((m) => ({
    default: m.FKSimilarStartupsContainer,
  }))
);

const FKSectorBenchmarksContainer = lazy(() =>
  import("../containers/FundingKit/FKSectorBenchmarksContainer").then((m) => ({
    default: m.FKSectorBenchmarksContainer,
  }))
);

const FKEntityContainer = lazy(() =>
  import("../containers/FundingKit/EntityViews/FKEntityContainer").then((m) => ({
    default: m.default,
  }))
);

const FKASAccountSettings = lazy(() =>
  import(
    "../containers/FundingKit/containers/FKAccountSettings/FKASAccountSettings"
  ).then((m) => ({ default: m.FKASAccountSettings }))
);

const BIAssistantContainer = lazy(() =>
  import("../containers/Assistant/AssistantContainer").then((m) => ({
    default: m.BIAssistantContainer,
  }))
);

const ChatHistoryContainer = lazy(() =>
  import("../containers/Assistant/ChatHistoryContainer").then((m) => ({
    default: m.ChatHistoryContainer,
  }))
);

// Wrap each component with Suspense for consistent loading UX
const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<SpinnerView message="Loading..." />}>
    <Component />
  </Suspense>
);

export const fundingKitRoutes = [
  { path: "/", element: withSuspense(FundingKitContainer) },
  { path: "/investors-analysis", element: withSuspense(FKIAListContainer) },
  { path: "/call-for-applications", element: withSuspense(FKCFAContainer) },
  {
    path: "/call-for-applications/:id",
    element: withSuspense(FKCFAContainerView),
  },
  { path: "/events", element: withSuspense(FKEventsContainer) },
  { path: "/events/:id", element: withSuspense(FKEventDetails) },
  {
    path: "/research",
    element: withSuspense(FundingKitResearchInsightsContainer),
  },
  {
    path: "/research/:index/:id",
    element: withSuspense(FundingKitResearchInsightView),
  },
  {
    path: "/similar-organisations",
    element: withSuspense(FKSimilarStartupsContainer),
  },
  { path: "/deals", element: withSuspense(FKDealsContainer) },
  { path: "/industry-pulse", element: withSuspense(FKIndustryPulseContainer) },
  {
    path: "/market-intelligence",
    element: withSuspense(FKMarketIntelligenceContainer),
  },
  {
    path: "/market-intelligence/trends/:trendId",
    element: withSuspense(FKTrendDetailContainer),
  },
  {
    path: "/sector-benchmarks",
    element: withSuspense(FKSectorBenchmarksContainer),
  },
  { path: "/organizations/:id", element: withSuspense(FKEntityContainer) },
  { path: "/settings", element: withSuspense(FKASAccountSettings) },
  { path: "/assistant", element: withSuspense(BIAssistantContainer) },
  { path: "/assistant/c/:id", element: withSuspense(BIAssistantContainer) },
  { path: "/assistant/history", element: withSuspense(ChatHistoryContainer) },
];

// Export the upgrade route separately as it's used outside the main routes
export const FKUpgradeRoute = {
  path: "/upgrade",
  element: withSuspense(FKUpgradeContainer),
};
